import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Paper,
  Grid,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  Alert,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  FaRocket,
  FaEye,
  FaUsers,
  FaFileAlt,
  FaCheckCircle,
  FaEdit,
  FaPhone,
  FaDatabase,
  FaAddressBook,
  FaListAlt,
} from "react-icons/fa";
import { MdCheckCircle } from "react-icons/md";
import { styled, alpha } from "@mui/material/styles";
import { getHeader, toastError } from "../../utils/util";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import AutoSelectTemplate from "../../widgets/AutoSelectTemplate";
import SectionLabel from "../../widgets/SectionLabel";
import RcsTemplateById from "./RcsTemplateById";
import { GlassButton, GlassButtonLoading } from "../../themes/UiElements";
import ContactsTabs from "./ContactsTabs";

// ─── Styled ───────────────────────────────────────────────────────────────────

const StyledCard = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  marginBottom: theme.spacing(3),
}));

const StepIcon = styled(Box)(({ theme, active, completed }) => ({
  width: 40,
  height: 40,
  borderRadius: "50%",
  background: completed
    ? "rgba(255,255,255,1)"
    : active
    ? "rgba(255,255,255,0.9)"
    : "rgba(255,255,255,0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: completed || active ? "#667eea" : "white",
  fontWeight: "bold",
  transition: "all 0.25s ease",
}));

const PreviewCard = styled(Card)(({ theme }) => ({
  background: "linear-gradient(45deg, #f5f7fa 0%, #c3cfe2 100%)",
  minHeight: 400,
  display: "flex",
  flexDirection: "column",
}));

const SummaryBar = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  background: alpha(theme.palette.primary.main, 0.06),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  marginBottom: theme.spacing(2.5),
}));

const SummaryItem = ({ icon, label, value }) => (
  <Box display="flex" alignItems="center" gap={0.75}>
    <Box sx={{ color: "primary.main", fontSize: 13, display: "flex" }}>
      {icon}
    </Box>
    <Typography variant="caption" color="textSecondary">
      {label}:
    </Typography>
    <Typography variant="caption" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

// ─── Contact source helper ────────────────────────────────────────────────────

function contactSourceLabel(campaignData, selectedCount) {
  if (campaignData.selectedGroup) {
    const rangeText =
      campaignData.startIndex !== undefined &&
      campaignData.endIndex !== undefined
        ? ` · ${campaignData.startIndex}–${campaignData.endIndex}`
        : "";
    if (campaignData.dbGroup === "contacts")
      return {
        icon: <FaAddressBook />,
        label: "Contact Group",
        text: `${campaignData.selectedGroup}${rangeText}`,
      };
    if (campaignData.dbGroup === "database")
      return {
        icon: <FaDatabase />,
        label: "Database List",
        text: `${campaignData.selectedGroup}${rangeText}`,
      };
    return {
      icon: <FaListAlt />,
      label: "Group",
      text: campaignData.selectedGroup,
    };
  }
  if (campaignData.contacts.length > 0)
    return {
      icon: <FaUsers />,
      label: "Contacts",
      text: `${selectedCount} / ${campaignData.contacts.length} selected`,
    };
  return null;
}

// ─── Component ────────────────────────────────────────────────────────────────

const Rcs2CampaignLauncher = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Campaign data (confirmed/submitted values) ───────────────────────────

  const [campaignData, setCampaignData] = useState({
    campaignName: "",
    template: "",
    message: "",
    contacts: [],
    uploadedFile: null,
    templateContent: null,
  });
  const [contactGroupName, setContactGroupName] = useState("");
  const [campaignGroupName, setCampaignGroupName] = useState("");

  // ── Transient ContactsTabs UI state — lifted so it persists on Back→Next ─
  //    Nothing here is "confirmed"; it's just what the user is currently
  //    looking at / typing in inside the Contacts step.

  const [contactsTabState, setContactsTabState] = useState({
    tabValue: 0,
    // Tab 1 – Database
    selectedList: "",
    dbColumns: [],
    dbStartIndex: "",
    dbEndIndex: "",
    // Tab 3 – Contacts List
    pendingGroup: "",
    groupSearchQuery: "",
    groupStartIndex: "",
    groupEndIndex: "",
  });

  // ── Stepper ──────────────────────────────────────────────────────────────

  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Select Template", "Upload Contacts", "Preview & Launch"];

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  // ── Misc UI state ────────────────────────────────────────────────────────

  const [isDragged, setIsDragged] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const contactsT = searchParams.get("contactsType");
    setContactGroupName(searchParams.get("name"));
    setCampaignGroupName(searchParams.get("groupName"));
    setContactsTabState((prev) => ({
      ...prev,
      tabValue:
        contactsT === "contacts" ? 3 : contactsT === "database" ? 1 : undefined,
    }));
  }, [searchParams]);

  // ── Derived ──────────────────────────────────────────────────────────────

  const selectedCount = campaignData.contacts.filter((c) => c.selected).length;
  const allSelected =
    selectedCount === campaignData.contacts.length &&
    campaignData.contacts.length > 0;
  const indeterminate = selectedCount > 0 && !allSelected;
  const filteredContacts = campaignData.contacts.filter((c) =>
    String(c.phone).includes(searchQuery)
  );

  const step0Done = !!campaignData.template && !!campaignData.campaignName;
  const step1Done = selectedCount > 0 || !!campaignData.selectedGroup;
  const contactSource = contactSourceLabel(campaignData, selectedCount);

  const nextDisabledReason = (() => {
    if (activeStep === 0) {
      if (!campaignData.campaignName)
        return "Enter a campaign name to continue";
      if (!campaignData.template) return "Select a template to continue";
    }
    if (activeStep === 1) {
      if (!step1Done)
        return "Add contacts or select a contact group to continue";
    }
    return null;
  })();

  // ── File / drag handlers (unchanged) ─────────────────────────────────────

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([["phoneNumber", "name"]]);
    XLSX.utils.book_append_sheet(wb, ws, "Contacts");
    XLSX.writeFile(wb, "contacts_template.xlsx");
  };

  const handleFileUpload = (fileOrEvent, duplicateChecker) => {
    const file =
      fileOrEvent instanceof File
        ? fileOrEvent
        : fileOrEvent?.target?.files?.[0];
    if (!file) return;
    if (
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setCampaignData((prev) => ({ ...prev, uploadedFile: file }));
      setUploadProgress(0);
      const reader = new FileReader();
      reader.onprogress = (e) => {
        if (e.lengthComputable)
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
      reader.onload = (e) => {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const parsedData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        let contacts = parsedData
          .slice(1)
          .map((row, index) => ({
            id: index + 1,
            phone: String(row[0] || ""),
            name: row[1] ? String(row[1]) : "Unknown",
            selected: true,
          }))
          .filter((c) => c.phone);
        if (duplicateChecker && typeof duplicateChecker === "function")
          contacts = duplicateChecker(contacts);
        setCampaignData((prev) => ({
          ...prev,
          contacts: [...prev.contacts, ...contacts],
          selectedGroup: "",
        }));
        setUploadProgress(100);
      };
      reader.readAsBinaryString(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragged(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragged(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragged(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) handleFileUpload({ target: { files: [files[0]] } });
  };

  const removeContact = (id) =>
    setCampaignData((prev) => ({
      ...prev,
      contacts: prev.contacts.filter((c) => c.id !== id),
    }));

  const toggleSelect = (id) =>
    setCampaignData((prev) => ({
      ...prev,
      contacts: prev.contacts.map((c) =>
        c.id === id ? { ...c, selected: !c.selected } : c
      ),
    }));

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setCampaignData((prev) => ({
      ...prev,
      contacts: prev.contacts.map((c) => ({ ...c, selected: checked })),
    }));
  };

  // ── Launch (unchanged logic + dbGroup in payload) ────────────────────────

  const launchCampaign = async () => {
    if (!campaignData.campaignName) {
      toast.error("Campaign name is required!");
      return;
    }
    if (!campaignData.template) {
      toast.error("Template is required!");
      return;
    }
    if (!campaignData.selectedGroup && campaignData.contacts.length < 1) {
      toast.error("Contacts List is required!");
      return;
    }
    try {
      setSendingCampaign(true);
      const response = await axios.post(
        `${API_ENDPOINT}/rcs/launch-campaign-2`,
        {
          contacts: campaignData.contacts,
          selectedGroup: campaignData.selectedGroup,
          dbGroup: campaignData.dbGroup,
          campaignName: campaignData.campaignName,
          startIndex: campaignData?.startIndex,
          endIndex: campaignData?.endIndex,
          templateId: campaignData.template.templateId,
          contactGroupName,
          campaignGroupName,
        },
        getHeader()
      );
      toast.success(response.data.message);
      setTimeout(() => navigate(`/home/queue-monitor?type=rcs`), 700);
    } catch (error) {
      toastError(error);
    } finally {
      setSendingCampaign(false);
    }
  };

  const handleCampaignName = useCallback((e) => {
    const value = e.target.value;
    if (/^[A-Za-z0-9_ ]*$/.test(value) && value.length <= 100)
      setCampaignData((prev) => ({ ...prev, campaignName: value }));
  }, []);

  // ── Template preview (unchanged) ─────────────────────────────────────────

  const renderTemplatePreview = (content) => {
    if (!content) return null;
    return (
      <Paper variant="outlined" sx={{ p: 3, bgcolor: "white", mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          {content.cardTitle}
        </Typography>
        {content.cardMedia && (
          <img
            src={content.cardMedia.contentInfo.fileUrl}
            alt="Media"
            style={{ maxWidth: "100%", height: "auto", marginBottom: 16 }}
          />
        )}
        <Typography style={{ whiteSpace: "pre-wrap" }} gutterBottom>
          {content.cardDescription}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {content.suggestions.map((sug, i) => (
            <Button key={i} variant="contained">
              {sug.action.plainText}
            </Button>
          ))}
        </Box>
      </Paper>
    );
  };

  // ── Step content ──────────────────────────────────────────────────────────

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <TextField
              label="Campaign Name"
              type="text"
              size="small"
              sx={{ my: 2 }}
              fullWidth
              helperText="Only letters, space, underscore and numbers allowed (max 100 chars)"
              value={campaignData.campaignName}
              onChange={handleCampaignName}
            />
            <SectionLabel>Choose a template for your RCS campaign</SectionLabel>
            <Box my={2}>
              <AutoSelectTemplate
                label="Select Template"
                value={selectedTemplate}
                onChange={(newV) => {
                  setCampaignData((prev) => ({ ...prev, template: newV }));
                  setSelectedTemplate(newV);
                }}
              />
            </Box>
            {selectedTemplate && (
              <RcsTemplateById id={selectedTemplate.templateId} />
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            {/* Recap of step 0 */}
            {step0Done && (
              <SummaryBar>
                <SummaryItem
                  icon={<FaFileAlt />}
                  label="Campaign"
                  value={campaignData.campaignName}
                />
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <SummaryItem
                  icon={<FaCheckCircle />}
                  label="Template"
                  value={campaignData.template?.templateName || "—"}
                />
              </SummaryBar>
            )}
            <ContactsTabs
              campaignData={campaignData}
              setCampaignData={setCampaignData}
              tabState={contactsTabState}
              setTabState={setContactsTabState}
              isDragged={isDragged}
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              handleFileUpload={handleFileUpload}
              downloadTemplate={downloadTemplate}
              uploadProgress={uploadProgress}
              selectedCount={selectedCount}
              indeterminate={indeterminate}
              allSelected={allSelected}
              handleSelectAll={handleSelectAll}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filteredContacts={filteredContacts}
              toggleSelect={toggleSelect}
              removeContact={removeContact}
              campaignGroupName={campaignGroupName}
              contactGroupName={contactGroupName}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            {/* Recap of steps 0 + 1 */}
            {(step0Done || step1Done) && (
              <SummaryBar>
                {step0Done && (
                  <>
                    <SummaryItem
                      icon={<FaFileAlt />}
                      label="Campaign"
                      value={campaignData.campaignName}
                    />
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    <SummaryItem
                      icon={<FaCheckCircle />}
                      label="Template"
                      value={campaignData.template?.templateName || "—"}
                    />
                  </>
                )}
                {step1Done && contactSource && (
                  <>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    <SummaryItem
                      icon={contactSource.icon}
                      label={contactSource.label}
                      value={contactSource.text}
                    />
                  </>
                )}
              </SummaryBar>
            )}

            <Typography variant="h6" gutterBottom color="textSecondary">
              Preview your campaign before launching
            </Typography>

            <PreviewCard>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar sx={{ bgcolor: "primary.main" }}>
                    <FaPhone />
                  </Avatar>
                  <Typography variant="h6">RCS Message Preview</Typography>
                </Box>

                {renderTemplatePreview(campaignData.templateContent)}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" gutterBottom>
                  Campaign Summary
                </Typography>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Campaign name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {campaignData.campaignName || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Template
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {campaignData.template?.templateName || "Not selected"}
                    </Typography>
                  </Box>

                  {campaignData.selectedGroup ? (
                    <>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Source
                        </Typography>
                        <Chip
                          size="small"
                          icon={
                            campaignData.dbGroup === "contacts" ? (
                              <FaAddressBook style={{ fontSize: 11 }} />
                            ) : (
                              <FaDatabase style={{ fontSize: 11 }} />
                            )
                          }
                          label={
                            campaignData.dbGroup === "contacts"
                              ? "Contact Group"
                              : "Database List"
                          }
                          color={
                            campaignData.dbGroup === "contacts"
                              ? "secondary"
                              : "primary"
                          }
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Group / List
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {campaignData.selectedGroup}
                        </Typography>
                      </Box>
                      {(campaignData.startIndex !== undefined ||
                        campaignData.endIndex !== undefined) && (
                        <Box sx={{ gridColumn: "span 2" }}>
                          <Typography variant="body2" color="textSecondary">
                            Index range
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {campaignData.startIndex ?? "start"} →{" "}
                            {campaignData.endIndex ?? "end"}
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Contacts
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedCount} selected
                        {campaignData.contacts.length !== selectedCount
                          ? ` of ${campaignData.contacts.length}`
                          : ""}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </PreviewCard>

            <Alert severity="info" sx={{ mt: 2 }}>
              Review all details before launching. This action cannot be undone.
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Box sx={{ maxWidth: 800, margin: "auto", p: 3 }}>
      <StyledCard>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <FaRocket size={32} />
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Launch RCS Campaign
              </Typography>
              <Typography variant="subtitle1">
                Create and send rich communication services campaigns
              </Typography>
            </Box>
          </Box>

          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => {
              const completed = index < activeStep;
              const active = index === activeStep;
              let sublabel = null;
              if (completed) {
                if (index === 0 && campaignData.campaignName)
                  sublabel = campaignData.campaignName;
                if (index === 1 && contactSource) sublabel = contactSource.text;
              }
              return (
                <Step key={label}>
                  <StepLabel
                    StepIconComponent={() => (
                      <StepIcon active={active} completed={completed}>
                        {completed ? <MdCheckCircle size={22} /> : index + 1}
                      </StepIcon>
                    )}
                  >
                    <Typography variant="body2" sx={{ color: "white" }}>
                      {label}
                    </Typography>
                    {sublabel && (
                      <Typography
                        variant="caption"
                        title={sublabel}
                        sx={{
                          color: "rgba(255,255,255,0.75)",
                          display: "block",
                          maxWidth: 120,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          mx: "auto",
                        }}
                      >
                        {sublabel}
                      </Typography>
                    )}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </CardContent>
      </StyledCard>

      <Paper sx={{ p: 3, mt: 2 }}>
        {renderStepContent(activeStep)}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<FaEdit />}
          >
            Back
          </Button>

          <Box display="flex" alignItems="center" gap={1}>
            {nextDisabledReason && activeStep < steps.length - 1 && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ maxWidth: 200, textAlign: "right" }}
              >
                {nextDisabledReason}
              </Typography>
            )}
            {activeStep === steps.length - 1 ? (
              <GlassButtonLoading
                variant="contained"
                loading={sendingCampaign}
                onClick={launchCampaign}
                startIcon={<FaRocket />}
                size="large"
              >
                {typeof campaignGroupName === "string" &&
                campaignGroupName !== ""
                  ? "Save Campaign"
                  : "Launch Campaign"}
              </GlassButtonLoading>
            ) : (
              <Tooltip title={nextDisabledReason || ""} placement="top">
                <span>
                  <GlassButton
                    variant="contained"
                    onClick={handleNext}
                    disabled={!!nextDisabledReason}
                    endIcon={
                      activeStep === steps.length - 2 ? <FaEye /> : <FaUsers />
                    }
                  >
                    {activeStep === steps.length - 2 ? "Preview" : "Next"}
                  </GlassButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Campaign Preview</DialogTitle>
        <DialogContent />
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Rcs2CampaignLauncher;
